import { formatDate } from '@angular/common';
import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, inject } from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Entity } from 'src/app/core/models/entity.model';
import { DynamoPage, Pagination, PaginationDynamo, PaginationRequest } from 'src/app/core/models/pagination.model';
import { User } from 'src/app/core/models/user.model';
import { FormBuilder } from '@angular/forms';
import { GazeMapService } from 'src/app/core/services/gaze-map.service';
import { FitService } from 'src/app/core/services/fit.service';
import { PaginationService } from 'src/app/core/services/pagination.service';
import * as utils from 'src/app/shared/helpers/utils';
import { LicenseService } from 'src/app/core/services/license.service';
import { Category, PackageFilterConfig, PackageStatus } from 'src/app/core/models/package.model';
import { PackageType } from 'src/app/core/models/package-type.model';
import { ConfigService } from 'src/app/core/services/config.service';
import { DeviceService } from 'src/app/core/services/device.service';
import { EntityService } from 'src/app/core/services/entity.service';
import { UserService } from 'src/app/core/services/user.service';
import { TeamScopeService } from 'src/app/core/services/team-scope.service';
import { CustomValidators } from 'src/app/shared/helpers/custom-validators';
import { Team } from 'src/app/core/models/team.model';

@Component({
  selector: 'hbo-datatable-smart',
  templateUrl: './datatable-smart.component.html',
  styleUrls: ['./datatable-smart.component.scss']
})
export class DatatableSmartComponent implements OnInit, AfterViewInit, OnDestroy {

  private static readonly SQL_FILTER_TTL_MS = 24 * 60 * 60 * 1000;

  @Input() items!: Pagination<unknown> | PaginationDynamo<unknown>;
  @Input() columns!: string[];
  @Input() typeData!: string;
  @Output() reload = new EventEmitter<PaginationRequest>();
  @Input() paginationType: 'sql' | 'dynamo' = 'sql';
  @Input() showFilterPanel: boolean = false;
  @Input() showDateFilter: boolean = false;
  @Input() showPackageFilters: boolean = false;
  @Input() showDeviceFilters: boolean = false;
  @Input() page?: string;
  @Input() showTeamFilter: boolean = false;
  @Input() teams_filter: Team[] = [];
  @Input() entities_filter: Entity[] = [];
  @Input() types_filter: Array<{ value: string; label: string }> = [];
  @Input() package_types_filter: PackageType[] = [];
  @Input() status_filter: PackageStatus[] = [];
  @Input() category_filter: Category[] = [];
  @Input() prevDynamoKey!: string;
  @Input() lastDynamoKey!: string;
  @Input() pages: DynamoPage[] = [];

  @ViewChild('typeFilter') typeFilter!: ElementRef;
  selectedType: string | null = null;
  @ViewChild('packageTypeFilter') packageTypeFilter!: ElementRef;
  selectedPackageType: string | null = null;
  @ViewChild('entityFilter') entityFilter!: ElementRef;
  selectedEntity: string | null = '';
  @ViewChild('statusFilter') statusFilter!: ElementRef;
  selectedStatus: string | null = null;
  @ViewChild('categoryFilter') categoryFilter!: ElementRef;
  selectedCategory: string | null = null;

  selectedTeamId: number | null = null;
  currentDistributorId: number | null = null;
  hasMultipleDistributorsSelected = false;
  distributors: Entity[] = [];
  distributorTeams: Team[] = [];
  isLoadingDistributorTeams = false;

  get isAdmin(): boolean { return this.user?.roleId === 1; }
  get isDistributor(): boolean { return this.user?.entity?.entityTypeId === 2; }
  get isOpticalShop(): boolean { return this.user?.entity?.entityTypeId === 4; }
  private readonly teamScope = inject(TeamScopeService);

  private teamsForUserSource: Team[] | null = null;
  private teamsForUserCache: Team[] = [];

  // Recomputed only when teams_filter itself changes: ng-select tracks [items]
  // by reference, and a fresh array on every change detection cycle makes it
  // rebuild and drop the selection being made.
  get teamsForUser(): Team[] {
    if (this.teams_filter !== this.teamsForUserSource) {
      this.teamsForUserSource = this.teams_filter;
      this.teamsForUserCache = this.teams_filter.filter(team => this.teamScope.isInScope(team.id));
    }
    return this.teamsForUserCache;
  }

  // Only pinned with exactly one team; with several the filter opens on "all teams".
  get fixedTeamId(): number | null {
    return this.teamScope.fixedTeamId();
  }
  readonly isTouchDevice = navigator.maxTouchPoints > 0;

  // Indica si la búsqueda ha sido confirmada (enter/lupa)
  searchApplied: boolean = false;

  get hasFiltersVisible(): boolean {
    return (this.showTeamFilter && (this.isAdmin || this.isDistributor)) ||
      this.showPackageFilters ||
      this.showDeviceFilters ||
      (this.paginationType === 'dynamo' && (this.types_filter?.length ?? 0) > 0);
  }

  get hasEntityOrSearchFilters(): boolean {
  
    let persisted: any = null;
    if (this.paginationType === 'dynamo') {
      // GazeMaps
      if (this.typeData === 'gazeMaps') {
        const raw = localStorage.getItem('mimesysFilter');
        if (raw) persisted = JSON.parse(raw);
      } else if (this.typeData === 'fits') {
        const raw = localStorage.getItem('fitFilter');
        if (raw) persisted = JSON.parse(raw);
      }
    } else {
      // SQL
      const filterKey = this.getFilterStorageKey();
      if (filterKey) {
        const raw = localStorage.getItem(filterKey);
        if (raw) persisted = JSON.parse(raw);
      }
    }
    if (this.typeData === 'gazeMaps') {
      const applied = this.configSvc.currentConfigValue.gazeMapsEntitiesSelectedApplied;
      if (Array.isArray(applied) && applied.length > 0) {
        return true;
      }
    }
    if (this.typeData === 'fits') {
      const applied = this.configSvc.currentConfigValue.fitsEntitiesSelectedApplied;
      if (Array.isArray(applied) && applied.length > 0) {
        return true;
      }
    }
    if (persisted && (this.hasMeaningfulValue(persisted.search) || this.hasMeaningfulValue(persisted.search_params))) {
      return true;
    }
    if (persisted?.filter_params) {
      const fp = typeof persisted.filter_params === 'string'
        ? JSON.parse(persisted.filter_params)
        : persisted.filter_params;
      if (this.hasMeaningfulValue(fp?.startDate) || this.hasMeaningfulValue(fp?.endDate)) {
        return true;
      }
    }
    return this.hasEntityFilters;
  }

  get hasEntityFilters(): boolean {
    return this.getAppliedEntitySelectionCount() > 0;
  }

  private getVirtualScrollSelectedKey(): string {
    const keyMap: { [key: string]: string } = {
      'package': 'packageEntitiesSelected',
      'devicesList': 'devicesListSelected',
      'usersList': 'usersListSelected',
      'settingsList': 'settingsListSelected',
      'entitiesList': 'entitiesListSelected',
      'policiesList': 'policiesListSelected',
    };
    return keyMap[this.typeData] || '';
  }

  private getVirtualScrollAppliedKey(): string {
    const keyMap: { [key: string]: string } = {
      'package': 'packageEntitiesSelectedApplied',
      'devicesList': 'devicesListSelectedApplied',
      'usersList': 'usersListSelectedApplied',
      'settingsList': 'settingsListSelectedApplied',
      'entitiesList': 'entitiesListSelectedApplied',
      'policiesList': 'policiesListSelectedApplied',
    };
    return keyMap[this.typeData] || '';
  }

  private getAppliedEntitySelectionCount(): number {
    const appliedKey = this.getVirtualScrollAppliedKey();
    const appliedSelection = appliedKey ? this.configSvc.currentConfigValue[appliedKey] : null;
    if (Array.isArray(appliedSelection)) {
      return appliedSelection.length;
    }

    const selectedKey = this.getVirtualScrollSelectedKey();
    const selectedSelection = selectedKey ? this.configSvc.currentConfigValue[selectedKey] : null;
    if (Array.isArray(selectedSelection)) {
      return selectedSelection.length;
    }

    return 0;
  }

  private getFilterStorageKey(): string {
    const keyMap: { [key: string]: string } = {
      'devicesList': 'devicesFilter',
      'package': 'packageFilter',
      'entitiesList': 'entitiesListFilter',
      'settingsList': 'settingsListFilter',
      'usersList': 'usersListFilter',
      'policiesList': 'policiesListFilter',
    };
    return keyMap[this.typeData] ?? '';
  }

  private saveFilterState(): void {
    const filterKey = this.getFilterStorageKey();
    if (!filterKey || this.paginationType === 'dynamo') return;
    this.pagination['teamId'] = this.selectedTeamId;
    if (this.isAdmin) {
      this.pagination['distributorId'] = this.currentDistributorId;
    }
    const forStorage = (this.pagination.filter_params && typeof this.pagination.filter_params === 'object')
      ? { ...this.pagination, filter_params: JSON.stringify(this.pagination.filter_params) }
      : this.pagination;
    this.persistSqlFilter(filterKey, forStorage);
  }

  private persistSqlFilter(filterKey: string, payload: any): void {
    localStorage.setItem(filterKey, JSON.stringify({ ...payload, _savedAt: Date.now() }));
  }

  private hasMeaningfulValue(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }

  private normalizeFilterParams(filterParams: any): any {
    const parsed = typeof filterParams === 'string'
      ? (() => {
          try {
            return JSON.parse(filterParams);
          } catch {
            return null;
          }
        })()
      : filterParams;

    if (!parsed || typeof parsed !== 'object') return null;

    const cleaned = Object.keys(parsed).reduce((acc: any, key: string) => {
      const value = parsed[key];
      if (this.hasMeaningfulValue(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    return Object.keys(cleaned).length ? cleaned : null;
  }

  private hasMeaningfulPersistedState(source: any): boolean {
    if (!source) return false;
    const normalizedFilterParams = this.normalizeFilterParams(source.filter_params);
    return this.hasMeaningfulValue(source.search) ||
      this.hasMeaningfulValue(source.search_params) ||
      this.hasMeaningfulValue(source.entities) ||
      this.hasMeaningfulValue(source.teamId) ||
      this.hasMeaningfulValue(source.distributorId) ||
      !!normalizedFilterParams;
  }

  private clearCurrentSqlFilterState(): void {
    delete this.pagination.filter_params;
    this.pagination.entities = null;
    this.pagination['teamId'] = null;
    this.pagination['distributorId'] = null;
    this.selectedTeamId = null;
    this.currentDistributorId = null;
    this.filters_applyed = false;
  }

  private sanitizePersistedSqlFilterState(): void {
    if (this.paginationType !== 'sql') return;

    const filterKey = this.getFilterStorageKey();
    if (!filterKey) return;

    const raw = localStorage.getItem(filterKey);
    if (!raw) return;

    let saved: any;
    try {
      saved = JSON.parse(raw);
    } catch {
      localStorage.removeItem(filterKey);
      this.clearCurrentSqlFilterState();
      return;
    }

    const savedAt = Number(saved?._savedAt ?? 0);
    if (savedAt && Date.now() - savedAt > DatatableSmartComponent.SQL_FILTER_TTL_MS) {
      localStorage.removeItem(filterKey);
      this.clearCurrentSqlFilterState();
      return;
    }

    const normalizedFilterParams = this.normalizeFilterParams(saved?.filter_params);
    const normalizedSaved = { ...saved, filter_params: normalizedFilterParams };

    if (!this.hasMeaningfulPersistedState(normalizedSaved)) {
      localStorage.removeItem(filterKey);
      this.clearCurrentSqlFilterState();
      return;
    }

    const currentNormalized = this.normalizeFilterParams(this.pagination.filter_params);
    this.pagination.filter_params = currentNormalized;

    if (!this.hasMeaningfulPersistedState(this.pagination)) {
      this.clearCurrentSqlFilterState();
    }

    if (normalizedFilterParams !== saved?.filter_params) {
      this.persistSqlFilter(filterKey, normalizedSaved);
    }
  }

  detectAndLoadDistributorTeams(restoreTeamId: number | null = null, fallbackDistributorId: number | null = null) {
    if (!this.isAdmin || !this.showTeamFilter) return;
    const key = this.getVirtualScrollSelectedKey();
    const selected: any[] = this.configSvc.currentConfigValue[key];
    let distributorId: number | null = null;
    let selectedDistributorsCount = 0;
    if (selected?.length) {
      const selectedDistributors = this.distributors.filter(d => selected.some((s: any) => s.id === d.id));
      selectedDistributorsCount = selectedDistributors.length;
      if (selectedDistributorsCount === 1) {
        distributorId = selectedDistributors[0].id;
      }
    }
    if (!distributorId && fallbackDistributorId) {
      distributorId = fallbackDistributorId;
    }
    this.hasMultipleDistributorsSelected = selectedDistributorsCount > 1;
    if (distributorId === this.currentDistributorId && !restoreTeamId) return;
    this.currentDistributorId = distributorId;
    this.distributorTeams = [];
    if (restoreTeamId && distributorId) {
      // Restore synchronously: the table is recreated on every reload, so waiting for
      // getTeams left teamId null and the next page change persisted it as null.
      this.selectedTeamId = restoreTeamId;
      this.pagination['teamId'] = restoreTeamId;
      this.filters_applyed = true;
    } else {
      this.selectedTeamId = null;
      this.pagination['teamId'] = null;
      if (!distributorId) {
        this.filtersForm.patchValue({ pendingAssignment: null });
        this.filtersForm.get('pendingAssignment')?.disable();
      }
    }
    // Persist after the team state is settled so a restored team is not overwritten with null
    this.saveFilterState();
    if (distributorId) this.filtersForm.get('pendingAssignment')?.enable();
    if (distributorId) {
      this.isLoadingDistributorTeams = true;
      this.userService.getTeams({ perPage: -1, entityId: distributorId } as any, true).subscribe(res => {
        this.distributorTeams = res.data;
        this.isLoadingDistributorTeams = false;
        // Drop a restored team that no longer belongs to this distributor (ids may come as strings)
        const restoredTeamIsValid = this.distributorTeams.some(t => Number(t.id) === Number(restoreTeamId));
        if (restoreTeamId && !restoredTeamIsValid && this.selectedTeamId === restoreTeamId) {
          this.selectedTeamId = null;
          this.pagination['teamId'] = null;
          this.saveFilterState();
        }
      });
    }
  }

  entities_filter$: Observable<any[]> = of(this.entities_filter);
  apply_filters_pb!: boolean;
  entityPending = false;
  searchPending = false;
  filters_applyed!: boolean;
  private _searchTriggerTs = 0;

  onSearchInput(action: () => void): void {
    const now = Date.now();
    if (now - this._searchTriggerTs < 200) return;
    this._searchTriggerTs = now;
    action();
  }

  pagination: PaginationRequest = {
    page: 1,
    perPage: 10,
    sort: '',
    order: '',
    search: ''
  };

  packageLabelsMapping: any = {
    "id": "id",
    "distributor": "owner",
    "opticalShop": "assigned",
    "type": "name",
    "category": "category",
    "status": "status",
    "remainingTestDays": "testDays",
    "renewalDate": "renewalDate",
    "device": "deviceId"
  };

  deviceLabelsMapping: any = {
    "id": "id",
    "type": "deviceType",
    "snMac": "serialNumber",
    "entity": "entity",
    "entityParent": "entity.parentEntity",
    "linked": "linked",
    "hardwareVersion": "hardwareVersion",
    "frontendSwVersion": "frontendSwVersion",
    "backendSwVersion": "backendSwVersion",
    "lastActivity": "lastActivity",
    "appName": "appName"
  };

  MaxEndDateValue = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  MaxInitDateValue = this.MaxEndDateValue;

  showFilters = false;
  searchExpanded = false;

  packageFiltersMapping: PackageFilterConfig = new PackageFilterConfig();
  packageFiltersMapper: string[] = ['entities', 'packageType', 'status', 'category', 'packageInactive', 'pendingAssignment'];
  settingsFiltersMapper: string[] = ['type', 'appIds'];

  appsList = [
    { id: 'mimesys', name: 'Mimesys' },
    { id: 'novaai', name: 'NovaAi' },
    { id: 'kudos', name: 'KUDOS' },
    { id: 'vreyegesture', name: 'VR Eye Gesture' },
    { id: 'synthesis', name: 'SYNTHESIS' },
    { id: 'stepperoptiai', name: 'STEPPER OPTI AI' },
    { id: 'mimetika2', name: 'MimetikaVR' },
    { id: 'mimesysnewlook', name: 'Mimesys New Look' },
    { id: 'demomimesys', name: 'DEMO-mimesys' },
    { id: 'mimesysmainland', name: 'Mimesys Mainland' }
  ];

  filtersForm = this.formBuilder.nonNullable.group({
    startDate: formatDate(new Date().getDate() - 7, 'yyyy-MM-dd', 'en'),
    endDate: '',
    entities: '',
    type: null,
    deleted: false,
    search: '',
    packageType: null,
    status: null,
    category: null,
    packageInactive: null,
    pendingAssignment: { value: null, disabled: true },
    appIds: [],
    search_params: '',
    search_with_entity: ''
  });

  filtersLstnr: Subscription = new Subscription;

  user: User = utils?.localStorageGetItem('user');
  entity_disabled!: boolean;

  maxPages = 3;
  lastPageVisible = 0;
  last = 0;

  typeOptions!: string[];

  constructor(
    public configSvc: ConfigService,
    public formBuilder: FormBuilder,
    private licenseService: LicenseService,
    private gazeMapService: GazeMapService,
    private fitService: FitService,
    public dynamoPaginationService: PaginationService,
    private deviceService: DeviceService,
    private entityService: EntityService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    if (!this.isAdmin) this.filtersForm.get('pendingAssignment')?.enable();
    if (this.paginationType === 'dynamo') {
      const dynamoItems = this.items as PaginationDynamo<unknown>;
      this.filtersForm.get('startDate')!.addValidators(CustomValidators.maxIntervalOneyear(this.filtersForm));
      this.filtersLstnr = this.filtersForm.valueChanges.subscribe(filter => {
        if (!this.filtersForm.pristine) {
          this.apply_filters_pb = true;
        } else {
          switch (this.typeData) {
            case 'gazeMaps':
              if (this.configSvc.currentConfigValue.gazeMapsEntitiesSelected && this.configSvc.currentConfigValue.gazeMapsEntitiesSelected === this.configSvc.currentConfigValue.gazeMapsEntitiesSelectedApplied) this.apply_filters_pb = false;
              else this.apply_filters_pb = true;
              break;
            case 'fits':
              if (this.configSvc.currentConfigValue.fitsEntitiesSelected && this.configSvc.currentConfigValue.fitsEntitiesSelected === this.configSvc.currentConfigValue.fitsEntitiesSelectedApplied) this.apply_filters_pb = false;
              else this.apply_filters_pb = true;
              break;
            default:
              this.apply_filters_pb = false;
              break;
          }
        }
      });
      switch (this.typeData) {
        case 'gazeMaps': {
          const fp = this.gazeMapService.currentGazeMapConfigValue.pagination?.filter_params;
          this.filters_applyed = this.hasDynamoDrawerFilters(fp);
          break;
        }
        case 'fits': {
          const fp = this.fitService.currentFitConfigValue.pagination?.filter_params;
          this.filters_applyed = this.hasDynamoDrawerFilters(fp);
          break;
        }
        default:
          break;
      }
      this.entities_filter$ = of(this.entities_filter);
      this.pagination['roleId'] = dynamoItems.roleId;
      this.user.role?.id == 4
        ? this.entity_disabled = true
        : this.entity_disabled = false;
      this.pagination['role'] = dynamoItems.role;
      this.pagination.page = dynamoItems.currentPage;
      this.pagination.perPage = dynamoItems.perPage;
      this.pagination.sort = dynamoItems.sort;
      this.pagination.order = dynamoItems.order;
      this.pagination.search_params = dynamoItems.search_params;
      this.pagination['search_with_entity'] = dynamoItems.search_with_entity;
      this.pagination['dynamoPrevKey'] = dynamoItems.dynamoPrevKey;
      this.pagination['dynamoLastKey'] = dynamoItems.dynamoLastKey;
      this.pagination.filter_params = dynamoItems.filter_params;
      this.pagination['shortTestID'] = dynamoItems.shortTestId;
      !this.pagination.filter_params!['endDate']
        ? this.pagination.filter_params!['endDate'] = formatDate(new Date(), 'yyyy-MM-dd', 'en')
        : null;
      this.pagination.showFilters = dynamoItems.showFilters;
      this.showFilters = dynamoItems.showFilters!;
      this.pagination.selectorEntitiesSearch = dynamoItems.selectorEntitiesSearch;
      !this.pagination['dynamoLastKey'] ? this.last = this.pagination.page : null;
    } else {
      const sqlItems = this.items as Pagination<unknown>;
      this.pagination.page = sqlItems.currentPage;
      this.pagination.perPage = sqlItems.perPage;
      this.pagination.sort = sqlItems.sort;
      this.pagination.order = sqlItems.order;
      this.pagination.search = sqlItems.search;
      this.pagination.filter_params = sqlItems.filter_params;
      this.pagination.showFilters = sqlItems.showFilters;
      this.pagination.entities = sqlItems.entities;
      this.showFilters = sqlItems.showFilters!;
      this.pagination.shortTestId = sqlItems.shortTestID;
      this.loadFiltersFromItems();
      this.sanitizePersistedSqlFilterState();
      // Team filter state restoration
      if (this.showTeamFilter) {
        const filterKey = this.getFilterStorageKey();
        const savedFilter = filterKey ? JSON.parse(localStorage.getItem(filterKey) ?? 'null') : null;
        const savedTeamId: number | null = savedFilter?.teamId ?? null;
        const savedDistributorId: number | null = savedFilter?.distributorId ?? null;
        if (this.isAdmin) {
          this.entityService.getOnlyParents().subscribe(res => {
            this.distributors = res.data.filter((e: Entity) => e.entityTypeId === 2);
            this.detectAndLoadDistributorTeams(savedTeamId, savedDistributorId);
          });
        } else {
          if (this.fixedTeamId) {
            this.selectedTeamId = this.fixedTeamId;
            this.pagination['teamId'] = this.fixedTeamId;
          } else if (savedTeamId) {
            this.selectedTeamId = savedTeamId;
            this.pagination['teamId'] = savedTeamId;
            this.filters_applyed = true;
          }
        }
      }
      switch (this.page) {
        case 'settings':
          this.configSvc.setConfigPaginationParamValue(this.page, 'pagination', this.pagination);
          break;
        default:
          break;
      }
    }
  }

  private loadFiltersFromItems(): void {
    const sqlItems = this.items as Pagination<unknown>;
    switch (this.typeData) {
      case 'package':
        let packageFilterParams = sqlItems.filter_params ? (typeof sqlItems.filter_params === 'string' ? JSON.parse(sqlItems.filter_params as unknown as string) : sqlItems.filter_params) : null;
        this.pagination.filter_params = packageFilterParams;
        this.filtersForm.patchValue({
          packageType: packageFilterParams ? packageFilterParams['packageType'] : null,
          status: packageFilterParams ? packageFilterParams['status'] : null,
          category: packageFilterParams ? packageFilterParams['category'] : null,
          packageInactive: packageFilterParams ? packageFilterParams['packageInactive'] : null,
          pendingAssignment: packageFilterParams ? packageFilterParams['pendingAssignment'] ?? null : null,
        });
        if (packageFilterParams) {
          this.filters_applyed = this.packageFiltersMapper.some(key => {
            const v = packageFilterParams[key];
            return v !== null && v !== '' && v !== undefined && (!Array.isArray(v) || v.length > 0);
          });
          this.selectedPackageType = packageFilterParams['packageType'];
          this.selectedStatus = packageFilterParams['status'];
          this.selectedCategory = packageFilterParams['category'];
        } else {
          this.filters_applyed = false;
        }
        break;
      case 'devicesList':
        let devicesFilterParams = sqlItems.filter_params ? (typeof sqlItems.filter_params === 'string' ? JSON.parse(sqlItems.filter_params as unknown as string) : sqlItems.filter_params) : null;
        this.pagination.filter_params = devicesFilterParams;
        this.filtersForm.patchValue({
          type: devicesFilterParams ? devicesFilterParams['type'] : null,
          appIds: devicesFilterParams ? devicesFilterParams['appIds'] : [],
        });
        if (devicesFilterParams) {
          this.filters_applyed = this.settingsFiltersMapper.some(key => {
            const v = devicesFilterParams[key];
            return v !== null && v !== '' && v !== undefined && (!Array.isArray(v) || v.length > 0);
          });
          this.selectedType = devicesFilterParams['type'];
        } else {
          this.filters_applyed = false;
        }
        break;
      default:
        break;
    }
  }

  ngAfterViewInit(): void {
    if (this.paginationType === 'dynamo') {
      this.filtersForm.patchValue({
        startDate: this.pagination.filter_params && this.pagination.filter_params['startDate'] ? this.pagination.filter_params['startDate'] : '',
        endDate: this.pagination.filter_params && this.pagination.filter_params['endDate'] ? this.pagination.filter_params['endDate'] : formatDate(new Date(), 'yyyy-MM-dd', 'en'),
        entities: this.pagination.filter_params && this.pagination.filter_params['entities'] ? this.pagination.filter_params['entities'] : [],
        type: this.pagination.filter_params && this.pagination.filter_params['type'] ? this.pagination.filter_params['type'] : null,
        deleted: this.pagination.filter_params && this.pagination.filter_params['deleted'] ? this.pagination.filter_params['deleted'] : false,
        search_params: '',
        search_with_entity: ''
      });
      this.MaxInitDateValue = this.pagination.filter_params && this.pagination.filter_params['endDate'] ? this.filtersForm.get('endDate')!.value : formatDate(new Date(), 'yyyy-MM-dd', 'en');
    }
    this.apply_filters_pb = false;
  }

  ngOnDestroy(): void {
    this.filtersLstnr.unsubscribe();
  }

  onPageChanged(page = 1) {
    this.pagination.page = page;
    this.apply_filters_pb = false;
    this.entityPending = false;
    this.searchPending = false;
    this.saveFilterState();
    this.reload.emit(this.pagination);
  }

  onSortChanged(column: string) {
    if (this.paginationType === 'dynamo') {
      switch (this.typeData) {
        case 'gazeMaps':
          this.dynamoPaginationService.resetGazeMapPagination();
          break;
        case 'fits':
          this.dynamoPaginationService.resetFitPagination();
          break;
        default:
          break;
      }
      delete this.pagination['dynamoPrevKey'];
      delete this.pagination['dynamoReverse'];
      delete this.pagination['dynamoLastKey'];
      if (this.pagination.sort === column) {
        if (this.pagination.order === 'asc') {
          this.pagination.order = 'desc';
        } else if (this.pagination.order === 'desc') {
          this.pagination.order = '';
          column = '';
        } else {
          this.pagination.order = 'asc';
        }
      } else {
        this.pagination.order = 'asc';
      }
      this.pagination.page = 1;
      this.pagination.sort = column;
      console.log(this.pagination);
      this.reload.emit(this.pagination);
    } else {
      if (this.mappLabelReverse(this.pagination.sort) == column) {
        if (this.pagination.order === 'asc') {
          this.pagination.order = 'desc';
        } else if (this.pagination.order === 'desc') {
          this.pagination.order = '';
          column = '';
        } else {
          this.pagination.order = 'asc';
        }
      } else {
        this.pagination.order = 'asc';
      }
      this.pagination.page = 1;
      switch (this.typeData) {
        case 'package':
          this.pagination.sort = this.packageLabelsMapping[column];
          break;
        case 'devicesList':
          this.pagination.sort = this.deviceLabelsMapping[column];
          break;
        default:
          this.pagination.sort = column;
          break;
      }
      this.reload.emit(this.pagination);
    }
  }

  onPerPageChanged(page = 1) {
    switch (this.typeData) {
      case 'package':
        break;
      case 'gazeMaps':
        this.dynamoPaginationService.resetGazeMapPagination();
        break;
      case 'fits':
        this.dynamoPaginationService.resetFitPagination();
        break;
      default:
        break;
    }
    this.pagination.page = page;
    if (this.paginationType === 'dynamo') {
      delete this.pagination['dynamoPrevKey'];
      delete this.pagination['dynamoReverse'];
      delete this.pagination['dynamoLastKey'];
    }
    this.reload.emit(this.pagination);
  }

  applyFilters() {
    this.apply_filters_pb = false;
    // Marcar búsqueda como aplicada solo si hay texto
    if (this.hasMeaningfulValue(this.pagination.search) || this.hasMeaningfulValue(this.pagination.search_params)) {
      this.searchApplied = true;
    } else {
      this.searchApplied = false;
    }
    if (this.paginationType === 'dynamo') {
      delete this.pagination['dynamoPrevKey'];
      delete this.pagination['dynamoReverse'];
      delete this.pagination['dynamoLastKey'];
      this.pagination.filter_params = this.filtersForm.value;
      this.filters_applyed = this.hasDynamoDrawerFilters(this.filtersForm.value);
      this.pagination.showFilters = false;
      this.pagination.page = 1;
      const entities_code_to_search = this.entities_filter.filter(
        entity => entity.name.includes(String(this.pagination.search_params))
      );
      let entities_filtered;
      switch (this.typeData) {
        case 'gazeMaps':
          entities_filtered = entities_code_to_search.map(entity => entity.id).join(" ");
          this.pagination['search_with_entity'] = entities_filtered;
          this.dynamoPaginationService.resetGazeMapPagination();
          this.configSvc.setConfigParamsValue('gazeMapsEntitiesSelectedApplied', this.configSvc.currentConfigValue.gazeMapsEntitiesSelected);
          localStorage.setItem('mimesysFilter', JSON.stringify(this.pagination));
          this.gazeMapService.setCurrentGazeMapParamConfig('pagination', this.pagination);
          break;
        case 'fits':
          entities_filtered = entities_code_to_search.map(entity => entity.internalCode).join(" ");
          this.pagination['search_with_entity'] = entities_filtered;
          this.dynamoPaginationService.resetFitPagination();
          this.configSvc.setConfigParamsValue('fitsEntitiesSelectedApplied', this.configSvc.currentConfigValue.fitsEntitiesSelected);
          localStorage.setItem('fitFilter', JSON.stringify(this.pagination));
          this.fitService.setCurrentFitParamConfig('pagination', this.pagination);
          break;
        default:
          break;
      }
      this.reload.emit(this.pagination);
    } else {
      switch (this.typeData) {
        case 'package':
          this.pagination.filter_params = {};
          Object.keys(this.filtersForm.value).map(key => {
            this.packageFiltersMapper.includes(key)
              ? this.pagination.filter_params![key] = this.filtersForm.get(key)!.value
              : null;
          });
          break;
        case 'devicesList':
          this.pagination.filter_params = {};
          Object.keys(this.filtersForm.value).map(key => {
            if (this.settingsFiltersMapper.includes(key)) {
              const value = this.filtersForm.get(key)!.value;
              if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
                this.pagination.filter_params![key] = value;
              }
            }
          });
          this.selectedType = this.filtersForm.get('type')!.value;
          break;
        default:
          if (this.showFilterPanel) {
            this.pagination.filter_params = this.filtersForm.value;
          } else {
            delete this.pagination.filter_params;
          }
          break;
      }
      this.pagination.showFilters = false;
      this.pagination['teamId'] = this.selectedTeamId;
      if (this.isAdmin) {
        this.pagination['distributorId'] = this.currentDistributorId;
      }
      switch (this.typeData) {
        case 'package':
          this.configSvc.setConfigParamsValue('packageEntitiesSelectedApplied', this.configSvc.currentConfigValue.packageEntitiesSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.packageEntitiesSelected;
          const packagePaginationForStorage = { ...this.pagination, filter_params: JSON.stringify(this.pagination.filter_params) };
          this.persistSqlFilter('packageFilter', packagePaginationForStorage);
          this.licenseService.setCurrentPackageParamConfig('pagination', this.pagination);
          break;
        case 'devicesList':
          this.configSvc.setConfigParamsValue('devicesListSelectedApplied', this.configSvc.currentConfigValue.devicesListSelected);
          if (this.configSvc.currentConfigValue.devicesListSelected?.length) {
            this.pagination.entities = `${this.configSvc.currentConfigValue.devicesListSelected?.map((entity: Entity) => entity.id).join(',')}`;
          } else {
            this.pagination.entities = null;
          }
          const devicesPaginationForStorage = { ...this.pagination, filter_params: JSON.stringify(this.pagination.filter_params) };
          this.persistSqlFilter('devicesFilter', devicesPaginationForStorage);
          this.deviceService.setCurrentDeviceParamConfig('pagination', this.pagination);
          break;
        case 'gazeMaps':
          this.dynamoPaginationService.resetGazeMapPagination();
          this.configSvc.setConfigParamsValue('gazeMapsEntitiesSelectedApplied', this.configSvc.currentConfigValue.gazeMapsEntitiesSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.gazeMapsEntitiesSelected;
          localStorage.setItem('mimesysFilter', JSON.stringify(this.pagination));
          this.gazeMapService.setCurrentGazeMapParamConfig('pagination', this.pagination);
          break;
        case 'fits':
          this.dynamoPaginationService.resetFitPagination();
          this.configSvc.setConfigParamsValue('fitsEntitiesSelectedApplied', this.configSvc.currentConfigValue.fitsEntitiesSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.fitsEntitiesSelected;
          localStorage.setItem('fitFilter', JSON.stringify(this.pagination));
          this.fitService.setCurrentFitParamConfig('pagination', this.pagination);
          break;
        case 'entitiesList':
          this.configSvc.setConfigParamsValue('entitiesListSelectedApplied', this.configSvc.currentConfigValue.entitiesListSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.entitiesListSelected?.length
            ? `${this.configSvc.currentConfigValue.entitiesListSelected.map((entity: Entity) => entity.id).join(',')}`
            : null;
          this.persistSqlFilter('entitiesListFilter', this.pagination);
          break;
        case 'usersList':
          this.configSvc.setConfigParamsValue('usersListSelectedApplied', this.configSvc.currentConfigValue.usersListSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.usersListSelected?.length
            ? `${this.configSvc.currentConfigValue.usersListSelected.map((entity: Entity) => entity.id).join(',')}`
            : null;
          this.persistSqlFilter('usersListFilter', this.pagination);
          break;
        case 'settingsList':
          this.configSvc.setConfigParamsValue('settingsListSelectedApplied', this.configSvc.currentConfigValue.settingsListSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.settingsListSelected?.length
            ? `${this.configSvc.currentConfigValue.settingsListSelected.map((entity: Entity) => entity.id).join(',')}`
            : null;
          this.persistSqlFilter('settingsListFilter', this.pagination);
          this.configSvc.setConfigPaginationParamValue('settings', 'pagination', this.pagination);
          break;
        case 'policiesList':
          this.configSvc.setConfigParamsValue('policiesListSelectedApplied', this.configSvc.currentConfigValue.policiesListSelected);
          this.pagination.entities = this.configSvc.currentConfigValue.policiesListSelected?.length
            ? `${this.configSvc.currentConfigValue.policiesListSelected.map((entity: Entity) => entity.id).join(',')}`
            : null;
          this.persistSqlFilter('policiesListFilter', this.pagination);
          break;
        default:
          break;
      }
      const hasFilterParams = this.pagination.filter_params != null &&
        Object.values(this.pagination.filter_params as any).some((v: any) =>
          v !== null && v !== '' && v !== undefined && (!Array.isArray(v) || v.length > 0)
        );
      this.filters_applyed = this.selectedTeamId != null || hasFilterParams;
      this.pagination.page = 1;
      this.reload.emit(this.pagination);
    }
  }

  cleanFilters() {
    switch (this.typeData) {
      case 'package':
        localStorage.removeItem('packageFilter');
        this.licenseService.setCurrentPackageParamConfig('pagination', null);
        this.licenseService.setCurrentPackageParamConfig('partners', null);
        this.licenseService.setCurrentPackageParamConfig('shops_selected', null);
        this.resetConfig('packageEntitiesSelected');
        break;
      case 'gazeMaps':
        this.dynamoPaginationService.resetGazeMapPagination();
        localStorage.removeItem('mimesysFilter');
        this.gazeMapService.setCurrentGazeMapParamConfig('pagination', null);
        this.gazeMapService.setCurrentGazeMapParamConfig('partners', null);
        this.gazeMapService.setCurrentGazeMapParamConfig('shops_selected', null);
        this.resetConfig('gazeMapsEntitiesSelected');
        break;
      case 'fits':
        this.dynamoPaginationService.resetFitPagination();
        localStorage.removeItem('fitFilter');
        this.fitService.setCurrentFitParamConfig('pagination', null);
        this.fitService.setCurrentFitParamConfig('partners', null);
        this.fitService.setCurrentFitParamConfig('shops_selected', null);
        this.resetConfig('fitsEntitiesSelected');
        break;
      case 'devicesList':
        localStorage.removeItem('devicesFilter');
        this.configSvc.setConfigPaginationParamValue('devices', 'pagination', null);
        this.deviceService.clearCurrentDeviceConfig();
        this.resetConfig('devicesListSelected');
        break;
      case 'entitiesList':
        localStorage.removeItem('entitiesListFilter');
        this.resetConfig('entitiesListSelected');
        break;
      case 'usersList':
        localStorage.removeItem('usersListFilter');
        this.resetConfig('usersListSelected');
        break;
      case 'settingsList':
        localStorage.removeItem('settingsListFilter');
        this.configSvc.setConfigPaginationParamValue('settings', 'pagination', null);
        this.resetConfig('settingsListSelected');
        break;
      case 'policiesList':
        localStorage.removeItem('policiesListFilter');
        this.resetConfig('policiesListSelected');
        break;
      default:
        break;
    }
    this.apply_filters_pb = false;
    this.filters_applyed = false;
    this.searchApplied = false;
    this.selectedTeamId = null;
    this.currentDistributorId = null;
    this.distributorTeams = [];
    this.pagination['teamId'] = null;
    this.pagination.search = '';
    this.pagination.page = 1;
    if (this.paginationType === 'dynamo') {
      this.pagination.filter_params!['starDate'] = null;
      this.pagination.search_params = '';
      this.pagination['search_with_entity'] = '';
      delete this.pagination['dynamoPrevKey'];
      delete this.pagination['dynamoReverse'];
      delete this.pagination['dynamoLastKey'];
    }
    delete this.pagination.filter_params;
    this.reload.emit(this.pagination);
  }

  private hasDynamoDrawerFilters(fp: any): boolean {
    if (!fp) return false;
    return (fp.type != null && fp.type !== '') || fp.deleted === true;
  }

  onTeamChanged(teamId: number | null) {
    this.selectedTeamId = teamId;
    this.apply_filters_pb = true;
  }



  // DynamoDB navigation methods
  dynamoPrev(_key: string) {
    this.pagination.page! -= 1;
    this.pagination['dynamoPrevKey'] = _key;
    this.pagination['dynamoReverse'] = true;
    this.reload.emit(this.pagination);
  }

  dynamoNext(_key: string) {
    this.pagination.page! += 1;
    this.pagination['dynamoLastKey'] = _key;
    this.pagination['dynamoReverse'] = false;
    this.reload.emit(this.pagination);
  }

  goDynamoPage(_page: number) {
    console.log('go page:' + _page);
    let matchPage: DynamoPage = this.pages.find((page: DynamoPage) => page.page == _page - 1)!;
    if (matchPage && _page != 1) {
      console.log(matchPage);
      this.pagination.page = _page;
      this.pagination['dynamoReverse'] = false;
      this.pagination['dynamoLastKey'] = matchPage.lastKey!;
      this.reload.emit(this.pagination);
    } else {
      this.firstPage();
    }
  }

  firstPage() {
    this.pagination.page = 1;
    delete this.pagination['dynamoPrevKey'];
    delete this.pagination['dynamoReverse'];
    delete this.pagination['dynamoLastKey'];
    this.reload.emit(this.pagination);
  }

  lastPage() {
    this.pagination.page = -1;
    this.pagination['dynamoReverse'] = true;
    this.prevDynamoKey = this.pagination['dynamoLastKey']!;
    this.reload.emit(this.pagination);
  }

  showPage(_page: number) {
    let match: DynamoPage = {};
    switch (this.typeData) {
      case 'gazeMaps':
        match = this.dynamoPaginationService.currentGazemapsPaginationValue.find((page: DynamoPage) => page.page == _page)!;
        break;
      case 'fits':
        match = this.dynamoPaginationService.currentFitPaginationValue.find((page: DynamoPage) => page.page == _page)!;
        break;
      default:
        break;
    }
    if (this.pagination.page == 1 && match && match.page! <= this.maxPages) {
      return true;
    } else if ((this.pagination.page! - this.maxPages >= _page) || (_page >= this.pagination.page! + this.maxPages - 1)) {
      return false;
    } else {
      this.lastPageVisible = _page;
      return true;
    }
  }

  showPrevPage() {
    if (this.pagination.page! > this.maxPages) {
      return true;
    } else {
      return false;
    }
  }

  goDynamoPageHide(_dir: string) {
    switch (_dir) {
      case 'prev':
        this.goDynamoPage(this.pagination.page! - this.maxPages);
        break;
      case 'next':
        this.goDynamoPage(this.lastPageVisible + 1);
        break;
    }
  }

  checkLastPageVisible() {
    let match: DynamoPage = {};
    switch (this.typeData) {
      case 'gazeMaps':
        match = this.dynamoPaginationService.currentGazemapsPaginationValue.find((page: DynamoPage) => page.page == this.lastPageVisible)!;
        break;
      case 'fits':
        match = this.dynamoPaginationService.currentFitPaginationValue.find((page: DynamoPage) => page.page == this.lastPageVisible)!;
        break;
      default:
        break;
    }
    if (!match) {
      return true;
    } else {
      if (match.lastKey) {
        return true;
      } else {
        return false;
      }
    }
  }

  // Filter utilities
  resetFilter(_filter: string) {
    this.apply_filters_pb = true;
    switch (_filter) {
      case 'entityFilter':
        this.selectedEntity = '';
        break;
      case 'typeFilter':
        this.selectedType = null;
        break;
      default:
        break;
    }
    this.pagination.page = 1;
  }

  callbackPartners(e: any) {
    if (e === 'change') {
      this.apply_filters_pb = true;
      this.entityPending = true;
      if (this.isAdmin && this.showTeamFilter && this.distributors.length) {
        this.detectAndLoadDistributorTeams();
      }
    }
  }

  callbackClickOutside(_e: any) {
    // Dropdown closed — no auto-apply; user clicks the search button to apply
  }

  applyEntitySearch() {
    this.pagination.page = 1;
    if (this.paginationType === 'dynamo') {
      this.pagination.filter_params = this.filtersForm.value;
      this.applyFilters();
      return;
    }
    switch (this.typeData) {
      case 'entitiesList':
        this.configSvc.setConfigParamsValue('entitiesListSelectedApplied', this.configSvc.currentConfigValue.entitiesListSelected);
        this.pagination.entities = this.configSvc.currentConfigValue.entitiesListSelected?.length
          ? `${this.configSvc.currentConfigValue.entitiesListSelected.map((entity: Entity) => entity.id).join(',')}`
          : null;
        break;
      case 'usersList':
        this.configSvc.setConfigParamsValue('usersListSelectedApplied', this.configSvc.currentConfigValue.usersListSelected);
        this.pagination.entities = this.configSvc.currentConfigValue.usersListSelected?.length
          ? `${this.configSvc.currentConfigValue.usersListSelected.map((entity: Entity) => entity.id).join(',')}`
          : null;
        break;
      case 'devicesList':
        this.configSvc.setConfigParamsValue('devicesListSelectedApplied', this.configSvc.currentConfigValue.devicesListSelected);
        this.pagination.entities = this.configSvc.currentConfigValue.devicesListSelected?.length
          ? `${this.configSvc.currentConfigValue.devicesListSelected.map((entity: Entity) => entity.id).join(',')}`
          : null;
        break;
      case 'settingsList':
        this.configSvc.setConfigParamsValue('settingsListSelectedApplied', this.configSvc.currentConfigValue.settingsListSelected);
        this.pagination.entities = this.configSvc.currentConfigValue.settingsListSelected?.length
          ? `${this.configSvc.currentConfigValue.settingsListSelected.map((entity: Entity) => entity.id).join(',')}`
          : null;
        break;
      case 'policiesList':
        this.configSvc.setConfigParamsValue('policiesListSelectedApplied', this.configSvc.currentConfigValue['policiesListSelected']);
        this.pagination.entities = this.configSvc.currentConfigValue['policiesListSelected']?.length
          ? `${this.configSvc.currentConfigValue['policiesListSelected'].map((entity: Entity) => entity.id).join(',')}`
          : null;
        break;
      case 'package':
        this.configSvc.setConfigParamsValue('packageEntitiesSelectedApplied', this.configSvc.currentConfigValue.packageEntitiesSelected);
        this.pagination.entities = this.configSvc.currentConfigValue.packageEntitiesSelected;
        break;
      default:
        break;
    }
    this.apply_filters_pb = false;
    this.entityPending = false;
    this.searchPending = false;
    this.saveFilterState();
    this.reload.emit(this.pagination);
  }

  toogleFilters() {
    this.showFilters = !this.showFilters;
    if (this.showFilters && this.isAdmin && this.showTeamFilter && this.distributors.length) {
      this.detectAndLoadDistributorTeams();
    }
  }

  onSearchIconClick(): void {
    if (this.isTouchDevice && this.searchPending) {
      if (this.paginationType === 'dynamo' || this.showFilterPanel) {
        this.applyFilters();
      } else {
        this.onPageChanged();
      }
    } else {
      this.toggleSearch();
    }
  }

  toggleSearch() {
    this.searchExpanded = !this.searchExpanded;
  }

  onChange(e: Event, _type?: string) {
    switch (_type) {
      case 'search':
        this.apply_filters_pb = true;
        this.searchPending = true;
        break;
      case 'startDate':
        this.pagination.filter_params!['startDate'] = e;
        this.entityPending = true;
        break;
      case 'endDate':
        this.pagination.filter_params!['endDate'] = e;
        this.entityPending = true;
        break;
      default:
        this.apply_filters_pb = true;
        break;
    }
  }

  onChange2(id: number, _type?: string) {
    switch (_type) {
      case 'entity':
        break;
      default:
        break;
    }
  }

  resetConfig = (_param: String) => {
    this.configSvc.setConfigParamsValue(`${_param}`, null);
    this.configSvc.setConfigParamsValue(`${_param}Applied`, null);
  }

  mappLabelReverse(sort?: string) {
    switch (this.typeData) {
      case 'package':
        return Object.keys(this.packageLabelsMapping)
          .filter(key => this.packageLabelsMapping[key] === sort);
      case 'devicesList':
        return Object.keys(this.deviceLabelsMapping)
          .filter(key => this.deviceLabelsMapping[key] === sort);
      default:
        return sort;
    }
  }

}
